import { useNavigate } from "react-router-dom";
import { Flame, PenLine, Users, Sparkles, HeartHandshake, MessagesSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LogoutButton from "@/components/LogoutButton";

const heroImage =
  "https://images.unsplash.com/photo-1571179769769-67dc96f56782?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const confessions = [
  {
    tag: "Letter I never sent",
    line:
      "Maa, I still keep the shawl you knit me in 2011. It smells like turmeric and safety and I open it on the nights the city gets too loud.",
    author: "WhisperingRain_42",
  },
  {
    tag: "Something I can’t say aloud",
    line:
      "I laughed at his joke on the metro. It’s the first time in seven months I remembered I was still a person, not just a widow.",
    author: "AmberDusk_18",
  },
  {
    tag: "Late night thought",
    line:
      "I told my therapist I was fine. I lied. But I told the internet the truth tonight, and somehow that counts.",
    author: "MidnightInk_07",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthed = !!(user && user !== false);

  const goStart = () => {
    if (isAuthed) navigate("/app/dashboard");
    else navigate("/auth?mode=signup");
  };
  const goMahfil = () => {
    if (isAuthed) navigate("/app/mahfil");
    else navigate("/auth?mode=login&next=/app/mahfil");
  };

  return (
    <div
      className="grain relative min-h-screen text-stone-100 overflow-x-hidden bg-cover bg-center bg-no-repeat bg-fixed"
      style={{
        backgroundImage:
          "url('https://customer-assets-v7afamib.emergentagent.net/wingman/4b71e223-37e4-4615-8453-e68a2ac08d9e/attachments/5c414267fd3e4c13ab99ab5618f1dedf_ChatGPT%20Image%20Sep%204%2C%202026%2C%2009_31_58%20AM.png')",
      }}
      data-testid="landing-root"
    >
      {/* Nav */}
      <header className="relative z-10 flex items-center justify-end px-6 sm:px-10 lg:px-16 py-6">
        <LogoutButton />
      </header>

      {/* Hero */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 pt-8 pb-16 lg:pt-12 lg:pb-20 max-w-5xl mx-auto text-center min-h-[calc(100vh-6rem)] flex flex-col justify-start">
        <div>
          <div className="rise-in inline-flex items-center gap-2 rounded-full border border-amber-900/40 bg-stone-900/60 px-3 py-1 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            <span className="font-mono-mini text-[10px] uppercase tracking-[0.28em] text-amber-200/80">
              Anonymous · Safe · Yours
            </span>
          </div>
          <h1
            className="rise-in font-serif-editorial text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-stone-50"
            data-testid="landing-hero-title"
          >
            Some feelings are best written
          </h1>
          <div
            className="rise-in-delay-2 mt-10 flex flex-col sm:flex-row sm:flex-nowrap gap-3 sm:gap-3 justify-center items-stretch"
          >
            {isAuthed ? (
              <>
                <button
                  data-testid="landing-goto-dashboard-btn"
                  onClick={() => navigate("/app/dashboard")}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent hover:bg-white/10 border border-white/40 hover:border-white/70 text-white font-semibold text-lg px-5 py-3 transition-all whitespace-nowrap"
                >
                  Your writing room
                  <PenLine className="w-5 h-5" />
                </button>
                <button
                  data-testid="landing-chatroom-btn"
                  onClick={goMahfil}
                  className="group relative shrink-0 inline-flex items-center justify-center gap-2 rounded-full text-white font-bold text-xl px-8 py-4 transition-all whitespace-nowrap overflow-visible bg-gradient-to-r from-amber-500 via-rose-500 to-fuchsia-600 hover:scale-105 shadow-[0_0_24px_-4px_rgba(244,63,94,0.7),0_0_48px_-12px_rgba(217,70,239,0.6)] chatroom-pulse"
                >
                  <span className="absolute inset-0 rounded-full ring-2 ring-white/40 animate-pulse pointer-events-none" />
                  <span className="absolute -top-2 -right-2 bg-white text-rose-600 text-[9px] font-mono-mini font-black uppercase tracking-widest px-2 py-0.5 rounded-full shadow-lg">
                    Live
                  </span>
                  <MessagesSquare className="w-6 h-6 relative" />
                  <span className="relative">ChatRoom</span>
                </button>
              </>
            ) : (
              <>
                <button
                  data-testid="landing-hero-cta-btn"
                  onClick={goStart}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent hover:bg-white/10 border border-white/40 hover:border-white/70 text-white font-semibold text-lg px-5 py-3 transition-all whitespace-nowrap"
                >
                  Start writing tonight
                  <PenLine className="w-5 h-5" />
                </button>
                <button
                  onClick={() => navigate("/auth?mode=login")}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-stone-700 hover:border-amber-500/60 text-stone-200 hover:text-amber-200 font-medium text-lg px-5 py-3 transition-all whitespace-nowrap"
                >
                  I’ve been here before
                </button>
                <button
                  data-testid="landing-login-btn"
                  onClick={() => navigate("/auth?mode=login")}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-stone-700 hover:border-amber-500/60 text-stone-200 hover:text-amber-200 font-medium text-lg px-5 py-3 transition-all whitespace-nowrap"
                >
                  Log in
                </button>
                <button
                  data-testid="landing-share-story-btn"
                  onClick={goStart}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-stone-700 hover:border-amber-500/60 text-stone-200 hover:text-amber-200 font-medium text-lg px-5 py-3 transition-all whitespace-nowrap"
                >
                  Share your story
                  <PenLine className="w-5 h-5" />
                </button>
                <button
                  data-testid="landing-chatroom-btn"
                  onClick={goMahfil}
                  className="group relative shrink-0 inline-flex items-center justify-center gap-2 rounded-full text-white font-bold text-xl px-8 py-4 transition-all whitespace-nowrap overflow-visible bg-gradient-to-r from-amber-500 via-rose-500 to-fuchsia-600 hover:scale-105 shadow-[0_0_24px_-4px_rgba(244,63,94,0.7),0_0_48px_-12px_rgba(217,70,239,0.6)] chatroom-pulse"
                >
                  <span className="absolute inset-0 rounded-full ring-2 ring-white/40 animate-pulse pointer-events-none" />
                  <span className="absolute -top-2 -right-2 bg-white text-rose-600 text-[9px] font-mono-mini font-black uppercase tracking-widest px-2 py-0.5 rounded-full shadow-lg">
                    Live
                  </span>
                  <MessagesSquare className="w-6 h-6 relative" />
                  <span className="relative">ChatRoom</span>
                </button>
              </>
            )}
          </div>

          <div className="rise-in-delay-3 mt-14 grid grid-cols-3 gap-9 max-w-md mx-auto">
            {[
              { icon: HeartHandshake, label: "No names" },
              { icon: Users, label: "Real people" },
              { icon: Sparkles, label: "Only truth" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-6">
                <Icon className="w-12 h-12 text-amber-400/80" />
                <span className="font-mono-mini text-[30px] uppercase tracking-[0.24em] text-stone-400">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Confessions strip */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 pb-24">
        <div className="max-w-2xl mb-10">
          <h2 className="font-serif-editorial text-3xl sm:text-4xl text-stone-100">
            Somewhere tonight, a stranger is writing what you feel.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {confessions.map((c, i) => (
            <article
              key={c.author}
              className="group relative rounded-2xl border border-amber-900/20 bg-stone-900/60 p-6 transition-all duration-500 hover:border-amber-500/40 hover:bg-stone-900/90 hover:-translate-y-1"
              style={{ animation: `rise 0.7s ease-out ${0.15 * i}s both` }}
            >
              <div className="font-mono-mini text-[10px] uppercase tracking-[0.28em] text-amber-300/70 mb-4">
                {c.tag}
              </div>
              <p className="font-serif-editorial text-xl leading-snug text-stone-100">
                {c.line}
              </p>
              <div className="mt-6 flex items-center justify-between text-stone-500 text-xs font-mono-mini">
                <span>— {c.author}</span>
                <Flame className="w-3.5 h-3.5 text-amber-400/70" />
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 pb-28">
        <div className="rounded-3xl border border-amber-900/30 bg-gradient-to-b from-stone-900/80 to-stone-950 p-10 sm:p-14 text-center max-w-3xl mx-auto">
          <Flame className="w-8 h-8 text-amber-400 mx-auto mb-6 candle-flicker" />
          <h3 className="font-serif-editorial text-3xl sm:text-4xl text-stone-100 leading-tight">
            The things you never said out loud — write them here.
          </h3>
          <p className="mt-4 text-stone-400">
            The publish button comes later. Tonight, just say the thing.
          </p>
          <button
            data-testid="landing-footer-cta-btn"
            onClick={goStart}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-transparent hover:bg-white/10 border border-white/40 hover:border-white/70 text-white font-semibold text-base px-7 py-4 transition-all"
          >
            Begin your first entry
            <PenLine className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-16 text-center font-mono-mini text-[10px] uppercase tracking-[0.3em] text-stone-600">
          kuchkahdo.com · a quiet corner of the internet
        </div>
      </section>
    </div>
  );
}
